/**
 * Canonical relative-time formatting for the client.
 *
 * The same `relativeTime` helper had been copy-pasted into ~10 page files
 * (conversations, intel-sources, projects, tasks). New code must import from
 * here; those copies should fold into this module as their files are touched.
 */

const MINUTE = 60_000;

function humanize(diffMs: number): string {
  const diffMin = Math.floor(diffMs / MINUTE);
  if (diffMin < 60) return `${Math.max(diffMin, 1)}m`;
  const diffH = Math.floor(diffMin / 60);
  if (diffH < 24) return `${diffH}h`;
  return `${Math.floor(diffH / 24)}d`;
}

/**
 * "just now" / "5m ago" / "3h ago" / "2d ago" for past instants.
 * Future instants render as "in 3h". Null/invalid input renders `fallback`.
 */
export function relativeTime(iso: string | null | undefined, fallback = '—'): string {
  if (!iso) return fallback;
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return fallback;

  const diffMs = Date.now() - then;
  if (diffMs < 0) return `in ${humanize(-diffMs)}`;
  if (diffMs < MINUTE) return 'just now';
  return `${humanize(diffMs)} ago`;
}

/** Absolute timestamp for `title` attributes, so hovering a relative time is exact. */
export function absoluteTime(iso: string | null | undefined): string | undefined {
  if (!iso) return undefined;
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? undefined : d.toLocaleString();
}
